import { getIdWithPrefix } from '@unifyapps/carbon/utils/id';
import { MatrixWebComponent } from 'src/matrixWebComponent';

const UA_ID_ATTRIBUTE = 'data-ua-id';

type Args = {
  externalContainerEl: HTMLElement;
  renderInShadowDom?: boolean;
  containerIdVsContainerEl: Map<string, HTMLElement>;
  containerIdVsMatrixWebComponent: Map<string, MatrixWebComponent>;
};

/**
 * Util to setup our `containerEl` on which our react root is mounted
 * `externalContainerEl` is the element with which our mount function is called
 * 1. Create a unique id for this externalContainerEl, so that for given externalContainerEl we only do setup once
 * 2. If `externalContainerEl` is UA element only, then we use it as is, otherwise we create a new container and append it to the externalContainerEl
 * 3. If `renderInShadowDom` is true, then we create a ua-dm web component and insert the containerEl into it
 */
export function setupContainerEl({
  externalContainerEl,
  renderInShadowDom,
  containerIdVsContainerEl,
  containerIdVsMatrixWebComponent,
}: Args) {
  // Set a unique id on the container element, if not exists already
  let containerId = externalContainerEl.getAttribute(UA_ID_ATTRIBUTE);
  if (!containerId) {
    // This logic will run exactly once for a given a externalContainerEl element, because we set the `UA_ID_ATTRIBUTE` on the container element first time
    // and run this logic only if `UA_ID_ATTRIBUTE` is not set
    containerId = getIdWithPrefix();
    externalContainerEl.setAttribute(UA_ID_ATTRIBUTE, containerId);

    let containerEl = externalContainerEl;
    const isUaDmElement = externalContainerEl.hasAttribute('ua-dm-element');
    // If the externalContainerEl is made by us only, then it will have `ua-dm-element` attribute set (for eg. via ua-web-sdk)
    // and its fine to use it as is.
    // But, if the externalContainerEl is not made by us and passed by end consumer, then we need to make a new container and append it to the externalContainerEl
    // and use that as our containerEl to mount the app.
    // Reason - `containerEl` is removed from main document and added to our ua-dm web component or shadow root
    //          and if the externalContainerEl is not made by us, then we should not move it to shadow root as then it will be inaccessible to parent app through JS
    // Note - `containerEl` is the element on which our react root is mounted, and containerEl is also moved inside ua-dm web component or shadow root
    if (!isUaDmElement) {
      containerEl = document.createElement('div');
      // giving height/width here so that scrolling works properly
      containerEl.style.width = '100%';
      containerEl.style.height = '100%';
      externalContainerEl.appendChild(containerEl);
    }

    containerIdVsContainerEl.set(containerId, containerEl);

    if (renderInShadowDom) {
      // Creating ua-dm web component for rendering in shadow dom here because we need to do it only first time
      // the mount function is called for a given container element
      const matrixWebComponent = document.createElement('ua-dm') as MatrixWebComponent;
      matrixWebComponent.insertElement(containerEl);
      containerIdVsMatrixWebComponent.set(containerId, matrixWebComponent);
    }
  }

  return containerId;
}
